import React, { useEffect, useState } from "react";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { RiSearch2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPagination,
  setPaginationReset,
} from "../../store/userSlice";
import { useSearchParams } from "react-router-dom";

let searchHandler;
let handleFilter;

export default function useSearchPagination(tableData = [], limit) {
  const [initialData, setInitialData] = useState(tableData);
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (tableData?.length > 0) {
      setInitialData(tableData);
      setCount(limit || tableData?.length);
    }
  }, [tableData]);

  const [searchParam, setSearchParam] = useSearchParams();
  const dispatch = useDispatch();
  const currentParams = {};
  for (let [key, value] of searchParam.entries()) {
    currentParams[key] = value;
  }

  const { currentPagination, paginationReset } = useSelector(
    (state) => state.user
  );
  const [resetPagination, setResetPagination] = useState(false);
  const [currentList, setCurrentList] = useState([]);

  const [searching, setSearching] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const [paginationInitialized, setPaginationIntialized] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    count: count || tableData?.length,
    totalData: tableData?.length,
    end() {
      return this.count * this.current;
    },
    start() {
      return this.end() - this.count;
    },
  });

  useEffect(() => {
    if (pagination.count !== 0) {
      setPaginationIntialized(true);
    }
  }, [pagination]);

  const [presetValues, setPresetValues] = useState([]); // Use a state variable for presetValues

  useEffect(() => {
    setPagination((prevState) => ({
      ...prevState,
      count: count || tableData?.length,
    }));

    // Calculate preset values: 10, 25, 50, 100, then total
    const maxPresetValue = tableData?.length;
    const steps = [10, 25, 50, 100];
    const calculatedPresetValues = steps.filter((s) => s < maxPresetValue);

    if (!calculatedPresetValues.includes(maxPresetValue) && maxPresetValue > 0) {
      calculatedPresetValues.push(maxPresetValue);
    }

    setPresetValues(calculatedPresetValues.length > 0 ? calculatedPresetValues : [maxPresetValue || 10]);
  }, [initialData]);

  useEffect(() => {
    setPagination((p) => ({ ...p, current: 1 }));
  }, [resetPagination]);

  useEffect(() => {
    // Removed setSearchParam to avoid adding ?page=1 query parameter to URL
    // setSearchParam(
    //   (previousParam) => {
    //     previousParam.set("page", pagination.current);
    //     return previousParam;
    //   },
    //   { replace: true }
    // );
    dispatch(setCurrentPagination(pagination.current));
  }, [pagination]);

  useEffect(() => {
    if (!paginationReset) {
      setPagination((p) => ({
        ...p,
        current: currentPagination,
      }));
      dispatch(setPaginationReset(true));
    }

    if (searchParam.get("page")) {
      setPagination((p) => ({
        ...p,
        current: +searchParam.get("page"),
      }));
    }
  }, []);

  // useEffect(() => {
  //   if (tableData?.length > 0 && pagination.count > 0) {
  //     setTotalPages(Math.ceil(tableData?.length / pagination.count));
  //     for (let i = pagination.start(); i < pagination.end(); i++) {
  //       if (i >= tableData?.length) {
  //         break;
  //       }
  //       setCurrentList((p) => [...p, tableData[i]]);
  //     }
  //   }
  //   return () => {
  //     setCurrentList([]);
  //   };
  // }, [tableData, paginationInitialized, pagination.current, pagination.count]);

  useEffect(() => {
    if (tableData?.length > 0 && pagination.count > 0) {
      setTotalPages(Math.ceil(tableData.length / pagination.count));

      const newList = [];
      for (let i = pagination.start(); i < pagination.end(); i++) {
        if (i >= tableData.length) break;
        newList.push(tableData[i]);
      }
      setCurrentList(newList);
    } else {
      setTotalPages(0);
      setCurrentList([]);
    }
  }, [tableData, pagination.current, pagination.count]);

  useEffect(() => {
    setSearching(false);
  }, [pagination.current]);

  searchHandler = (e, searchKey = [], setSearchWords) => {
    const searchTerm = e.target.value.trim(); // Trim the input to remove leading/trailing spaces
    const inputValue = e.target.value;
    const wordsArray = inputValue.split(" ");
    if (setSearchWords) {
      setSearchWords(wordsArray);
    }

    if (searchTerm.length > 0) {
      setResetPagination((prev) => !prev);
      setSearching(true);

      // Split the input value into individual search terms
      const searchTerms = searchTerm.split(/\s+/);
      // Check if all search terms are just spaces
      if (searchTerms.every((term) => term === "")) {
        // If all terms are spaces, show an empty result or handle it as you prefer
        setCurrentList([]);
      } else {
        // Initialize an array to store the results for each search term
        const searchResults = searchTerms.map((term) => {
          return tableData.filter((data) => {
            return searchKey.some((sKey) => {
              if (String(sKey).split(".").length > 1) {
                const word = findString(data, sKey);
                if (word) {
                  return String(word)
                    .toLowerCase()
                    .includes(String(term).toLowerCase());
                } else return false;
              } else {
                return String(data[sKey])
                  .toLowerCase()
                  .includes(String(term).toLowerCase());
              }
            });
          });
        });

        // Combine the results from all search terms
        const flattenResult = searchResults.flat();
        const uniqueResult = [
          ...new Map(flattenResult.map((item) => [item["id"], item])).values(),
        ];
        setCurrentList(uniqueResult);
      }
    } else {
      setSearching(false);
      setCurrentList([]);
      for (let i = pagination.start(); i < pagination.end(); i++) {
        if (i >= tableData.length) {
          break;
        }
        setCurrentList((p) => [...p, tableData[i]]);
      }
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const handleCountChange = (event) => {
    const newCount = parseInt(event.target.value, 10);
    setPagination((p) => ({ ...p, count: newCount, current: 1 }));
  };

  // Event handler to update pagination
  const handlePageChange = (event) => {
    const newPage = parseInt(event.target.value, 10);
    setPagination((p) => ({ ...p, current: newPage }));
  };

  const [filteringKeys, setFilteringKeys] = useState({});

  handleFilter = (filterBy, value, dateComparison, equation) => {
    if (value == "All") {
      delete filteringKeys[filterBy];
    } else {
      filteringKeys[filterBy] = value;
    }
    setFilteringKeys(filteringKeys);
    const filteredArray = customFilter(
      tableData,
      filteringKeys,
      dateComparison,
      equation
    );
    setCurrentList(filteredArray);
  };

  const Pagination = ({ className }) => {
    return (
      <>
        <div className={`flex flex-col items-center w-full ${className}`}>
          <div className="flex md:gap-10  sm:gap-4 justify-center items-center bg-white  p-1 text-center  rounded-md mb-1  shadow-md w-full">
            <button
              className={`p-2  rounded-md ${
                pagination.current > 1
                  ? "hover:bg-blue/90 hover:text-white "
                  : "cursor-not-allowed"
              }`}
              onClick={() => setPagination((p) => ({ ...p, current: 1 }))}
            >
              <MdKeyboardDoubleArrowLeft />
            </button>
            <button
              className={`p-2  rounded-md ${
                pagination.current > 1
                  ? "hover:bg-blue/90 hover:text-white "
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                pagination.current > 1 &&
                setPagination((p) => ({ ...p, current: p.current - 1 }))
              }
            >
              <MdKeyboardArrowLeft />
            </button>
            <select value={pagination.current} onChange={handlePageChange}>
              {getPageNumbers().map((pageNumber) => (
                <option key={pageNumber} value={pageNumber}>
                  Page {pageNumber}
                </option>
              ))}
            </select>

            {Object.keys(filteringKeys).length > 0 ? (
              <p>{currentList.length} per page</p>
            ) : (
              <select value={pagination.count} onChange={handleCountChange}>
                {presetValues.map((value) => (
                  <option key={value} value={value}>
                    {value} per page
                  </option>
                ))}
              </select>
            )}
            {/* Include an input field for custom values */}
            {/* <input
            type="number"
            value={pagination.count}
            onChange={handleCountChange}
            min={1}
            step={1}
          /> */}

            <button
              className={`p-2  rounded-md ${
                pagination.current < totalPages
                  ? "hover:bg-blue/90 hover:text-white "
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                pagination.current < totalPages &&
                setPagination((p) => ({ ...p, current: p.current + 1 }))
              }
            >
              <MdKeyboardArrowRight />
            </button>
            <button
              className={`p-2  rounded-md ${
                pagination.current < totalPages
                  ? "hover:bg-blue/90 hover:text-white "
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                setPagination((p) => ({ ...p, current: totalPages }))
              }
            >
              <MdKeyboardDoubleArrowRight />
            </button>
          </div>
          <p className="text-sm">
            Showing page {pagination.current} of {totalPages}
          </p>
        </div>
      </>
    );
  };

  const NoDataFound = ({ customClass, colSpan, className }) => {
    return (
      <>
        {currentList.length <= 0 && (
          <div
            className={`flex justify-center items-center gap-3 mt-5 w-full col-span-${
              colSpan || 5
            } ${customClass || className || ""}`}
          >
            <p>No Data Found </p>
          </div>
        )}
      </>
    );
  };

  const PaginationIndex = ({ index }) => {
    return <p>{searching ? index + 1 : pagination.start() + index + 1}</p>;
  };

  const CountPerPage = ({ className, parentClassName }) => {
    return (
      <div className={`w-full flex justify-end ${parentClassName}`}>
        <select
          value={pagination.count}
          onChange={handleCountChange}
          className={`select select-sm  ${className}`}
        >
          {presetValues.map((value) => (
            <option key={value} value={value}>
              {value} per page
            </option>
          ))}
        </select>
      </div>
    );
  };

  const ResetFilter = () => {
    setCurrentList(tableData);
    setFilteringKeys({});
  };

  return {
    Pagination,
    Search,
    NoDataFound,
    PaginationIndex,
    setResetPagination,
    CountPerPage,
    FilterColumn,
    ResetFilter,
    filteringKeys,
    currentList,
    paginationData: { searching, pagination },
  };
}

const Search = ({
  placeholder = "Enter to search",
  className,
  // searchHandler,
  searchKey,
  keywords,
  setSearchWords,
}) => {
  return (
    <div
      className={`max-w-fit w-full flex  items-center relative my-1 ${className}`}
    >
      <RiSearch2Line className="absolute mx-1 text-slate-400" />
      <input
        type="search"
        className={`input input-sm h-10 max-w-xs pl-6 w-full input-bordered focus:outline-none uppercase`}
        placeholder={placeholder}
        onChange={(e) => {
          searchHandler(e, searchKey || keywords, setSearchWords);
        }}
      />
    </div>
  );
};

const FilterColumn = ({
  filterBy,
  filterType,
  optionsArray,
  className,
  filteringKeys,
  dateComparison = false,
  equation = "=",
}) => {
  const monthMap = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  if (filterType == "date") {
    var inputDate = filteringKeys[filterBy];
    var parts = String(inputDate)?.split("-");
    var day = parts[0];
    var month = monthMap[parts[1]];
    var year = parts[2];
    var formattedDate = year + "-" + month + "-" + day;
    let value = formattedDate;

    return (
      <input
        type="date"
        value={value}
        className={`input input-sm input-bordered ${className}`}
        onChange={(e) => {
          handleFilter(
            filterBy,
            formatDate(e.target.value),
            dateComparison,
            equation
          );
        }}
      />
    );
  } else if (filterType == "time") {
    let value = filteringKeys[filterBy];
    return (
      <input
        type="time"
        value={value}
        className={`input input-sm input-bordered ${className}`}
        onChange={(e) =>
          handleFilter(
            filterBy,
            `${e.target.value}:00`,
            dateComparison,
            equation
          )
        }
      />
    );
  } else {
    return (
      <select
        className={`select select-sm select-bordered ${className}`}
        value={filteringKeys[filterBy] ? filteringKeys[filterBy] : "All"}
        onChange={(e) =>
          handleFilter(filterBy, e.target.value, dateComparison, equation)
        }
      >
        <option value="All">All</option>
        {optionsArray.map((opt, index) => (
          <option key={index} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
};

function findString(item, path) {
  if (!path) {
    return null;
  }

  const keys = path.split(".");
  let value = item;

  for (const key of keys) {
    if (value === null || value === undefined || !(key in value)) {
      return null;
    }

    value = value[key];
  }

  if (typeof value === "string") {
    return value;
  }

  return null;
}

function customFilter(array, criteria) {
  return array.filter((data) => {
    for (const key in criteria) {
      if (
        criteria[key] !== undefined &&
        String(data[key]).toLowerCase() !== String(criteria[key]).toLowerCase()
      ) {
        return false;
      }
    }
    return true;
  });

  // else if (dateComparison) {
  //   if (equation == "=") {
  //     if (
  //       new Date(data[key]).getTime() == new Date(criteria[key]).getTime()
  //     )
  //       return true;
  //     else return false;
  //   } else if (equation == "<") {
  //     if (
  //       new Date(data[key]).getTime() <= new Date(criteria[key]).getTime()
  //     )
  //       return true;
  //     else return false;
  //   } else if (equation == ">") {
  //     if (
  //       new Date(data[key]).getTime() >= new Date(criteria[key]).getTime()
  //     )
  //       return true;
  //     else return false;
  //   }
  // }
}

const formatDate = (date) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  var dateObject = new Date(date);
  var month = dateObject.getMonth();
  var day = dateObject.getDate();
  var year = dateObject.getFullYear();

  // Use padStart to ensure day has two digits
  var formattedDay = day.toString().padStart(2, "0");
  if (dateObject != "Invalid Date") {
    return `${formattedDay}-${months[month]}-${year}`;
  } else {
    return "All";
  }
};
